import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const NewsletterModal = ({ isOpen, onClose, onSubmit }) => {
  const [email, setEmail] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(email);
    setEmail('');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-slate-900/90 p-8 rounded-2xl shadow-2xl max-w-md w-full mx-4 backdrop-blur-md"
            onClick={e => e.stopPropagation()}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <h3 className="text-2xl font-bold text-blue-400 text-center">
                Join the Adventure!
              </h3>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="w-full px-4 py-3 rounded-lg bg-slate-800/90 border border-slate-700 text-white placeholder-slate-400 focus:border-blue-400 focus:outline-none transition-all duration-300"
                required
              />
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-blue-500 hover:bg-blue-400 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
              >
                Subscribe Now
              </motion.button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const AnimatedCharacter = ({ character, index }) => (
  <motion.span
    className="inline-block"
    whileHover={{ 
      scale: 1.2, 
      color: '#60A5FA',
      textShadow: '0 0 8px rgba(96, 165, 250, 0.5)'
    }}
    transition={{
      type: "spring",
      stiffness: 500,
      damping: 15,
      mass: 0.5
    }}
  >
    {character === ' ' ? '\u00A0' : character}
  </motion.span>
);

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showPrompt, setShowPrompt] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  
  const slides = [
    "/images/slide1.svg",
    "/images/slide2.svg",
    "/images/slide3.svg"
  ];

  useEffect(() => {
    setHasInitiallyLoaded(true);
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const handleSubscribe = (email) => {
    console.log('Subscribed with:', email);
    setShowModal(false);
    setShowPrompt(false);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.3
      }
    }
  };

  const childVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  return (
    <div className="h-screen w-full relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        {/* Background Images */}
        {slides.map((slide, index) => (
          <motion.div
            key={slide}
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${slide})`,
              zIndex: index === currentSlide ? 1 : 0,
            }}
            initial={{ 
              opacity: index === 0 ? 0 : 0,
              scale: index === 0 ? 1 : 1.05
            }}
            animate={{ 
              opacity: index === currentSlide ? 1 : 0,
              scale: index === currentSlide ? 1.15 : 1.05,
            }}
            transition={{
              opacity: !hasInitiallyLoaded && index === 0 
                ? { duration: 0.8, ease: "easeOut" }
                : { duration: 1.5, ease: "easeInOut" },
              scale: { duration: 5, ease: "linear" }
            }}
          />
        ))}

        {/* Special initial overlay that fades out quickly */}
        <motion.div
          className="absolute inset-0 bg-slate-900 z-[2]"
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        />

        {/* Regular overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-40 z-[2]" />

        <div className="absolute inset-0 z-[3]" style={{ opacity: 0.1 }}>
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                backgroundColor: '#4169E1',
                width: `${20 + Math.random() * 30}px`,
                height: `${20 + Math.random() * 30}px`,
              }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.1, 0.3, 0.1],
                rotate: [0, 180, 360],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut"
              }}
            />
          ))}
        </div>
      </div>

      <div className="absolute inset-0 flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="relative z-10 mt-24 sm:mt-0"
          >
            <motion.div className="relative mb-6 p-2">
              {/* Decorative top line */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 1, duration: 0.8 }}
                className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-0.5 bg-gradient-to-r from-transparent via-blue-400 to-transparent"
              />
              
              <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-wide 
                            bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent
                            relative"
                style={{
                  textShadow: '0 0 40px rgba(255,255,255,0.2), 0 0 20px rgba(96,165,250,0.4)'
                }}
              >
                {"NED AND NANCY".split('').map((char, index) => (
                  <AnimatedCharacter key={index} character={char} index={index} />
                ))}
              </h1>

              {/* Decorative bottom line */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 1, duration: 0.8 }}
                className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-0.5 bg-gradient-to-r from-transparent via-blue-400 to-transparent"
              />
            </motion.div>

            <motion.h2
              variants={childVariants}
              className="text-xl sm:text-2xl lg:text-3xl text-white tracking-wide mb-8
                         relative inline-block"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-200 via-white to-blue-200">
                HUMOROUS AND MYSTERIOUS
                <br />
                CHILDREN'S ADVENTURES
              </span>
              <motion.span
                className="absolute -bottom-2 left-0 w-full h-0.5"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 1.5, duration: 0.8 }}
              >
                <span className="block h-full bg-gradient-to-r from-transparent via-blue-400 to-transparent" />
              </motion.span>
            </motion.h2>
          </motion.div>
        </div>
      </div>

      {/* Sliding Newsletter Prompt */}
      <AnimatePresence>
        {showPrompt && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: "spring", damping: 20 }}
            className="fixed right-0 top-1/2 -translate-y-1/2 bg-slate-900/80 backdrop-blur-md p-6 rounded-l-2xl shadow-2xl max-w-sm z-20"
          >
            <button 
              onClick={() => setShowPrompt(false)}
              className="absolute top-2 right-2 text-slate-400 hover:text-white"
            >
              <X size={20} />
            </button>
            <h3 className="text-xl font-bold text-blue-400 mb-4">
              Want to Join the Adventure?
            </h3>
            <p className="text-white mb-6">
              Sign up for the Official Ned and Nancy Newsletter!
            </p>
            <div className="flex gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowModal(true)}
                className="flex-1 bg-blue-500 hover:bg-blue-400 text-white font-bold py-2 px-4 rounded-lg transition duration-200"
              >
                Yes!
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowPrompt(false)}
                className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-bold py-2 px-4 rounded-lg transition duration-200"
              >
                No
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <NewsletterModal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)}
        onSubmit={handleSubscribe}
      />
    </div>
  );
};

export default Hero;